import {TZDate} from "react-day-picker";

export const getOrdinal = (n: number): string => {
  if (n === 5) return "last";
  const suffixes: Record<number, string> = {1: "1st", 2: "2nd", 3: "3rd", 4: "4th"};
  return suffixes[n] ?? `${n}th`;
};

export const getDayOrdinal = (n: number): string => {
  if (n >= 11 && n <= 13) return `${n}th`;
  const lastDigit = n % 10;
  if (lastDigit === 1) return `${n}st`;
  if (lastDigit === 2) return `${n}nd`;
  if (lastDigit === 3) return `${n}rd`;
  return `${n}th`;
};

// The monthly recurrence day/weekday must be read in the account timezone, not the
// browser's. A start time late in the day (e.g. 21:00 EST) rolls over to the next
// calendar day in a browser east of the account (e.g. EU), which would otherwise
// derive the wrong day-of-month and weekday.
const zonedDate = (date: Date, timezone?: string): Date => (timezone ? new TZDate(date, timezone) : date);

export const getWeekOccurrenceInMonth = (date: Date, timezone?: string): number => {
  const day = zonedDate(date, timezone).getDate();
  return Math.ceil(day / 7);
};

export const isLastWeekdayOccurrence = (date: Date, timezone?: string): boolean => {
  const current = zonedDate(date, timezone);
  const nextWeek = timezone ? new TZDate(current, timezone) : new Date(current);
  nextWeek.setDate(nextWeek.getDate() + 7);
  return nextWeek.getMonth() !== current.getMonth();
};

export interface MonthlyRecurrenceParts {
  dayOfMonth: number;
  jsWeekday: number; // 0 (Sunday) .. 6 (Saturday), in the given timezone
  occurrence: number; // 1..5 — nth occurrence of the weekday within the month
  isLast: boolean; // whether this is the last occurrence of the weekday in the month
}

export const getMonthlyRecurrenceParts = (date: Date, timezone?: string): MonthlyRecurrenceParts => {
  const zoned = zonedDate(date, timezone);
  return {
    dayOfMonth: zoned.getDate(),
    jsWeekday: zoned.getDay(),
    occurrence: getWeekOccurrenceInMonth(date, timezone),
    isLast: isLastWeekdayOccurrence(date, timezone),
  };
};
